<script setup lang="ts">
import { Variants, Appearance } from '~/components/ui/Button.vue'
import { IconFields } from '../cms/Media/Icon.vue'

export type AnchorProps = {
  appearance?: Appearance
  variant?: Variants
  url?: string
  text?: string
  addToSiteNavigationSchema?: boolean
  newTab?: boolean
  icon?: IconFields
  gaOptions?: DataLayerOptional
  logHover?: boolean // @TODO
  logClick?: boolean
}

const props = withDefaults(defineProps<AnchorProps>(), {
  newTab: false,
  variant: 'none',
})

function getFallbackAppearance(variant: Variants) {
  return ['primary', 'secondary', 'secondary-reverse', 'tertiary'].includes(variant) ? 'Button' : 'Link'
}

const variant = props.variant?.replace('-button', '').replace('-link', '').replace('-text', '') as Variants // clean up variant names
const appearance = props.appearance || getFallbackAppearance(variant)
const classPrefix = `${appearance.toLowerCase()}-`
let classes = `${classPrefix}${variant}`
classes = props.variant === 'none' ? '' : classes

const targetObj = props.newTab ? { target: '_blank' } : {}
const { localizeURL } = useSiteUrl()
const localeURL = computed(() => localizeURL(props.url || ''))
const { registerSiteNavigationLink } = useSchemaOrg()

const { eventName, runtimeProps, info, ...restGaOptions } = props.gaOptions || {}
const anchorElement = ref<HTMLElement | null>(null)

if (import.meta.server && props.addToSiteNavigationSchema && props.text && props.url) {
  registerSiteNavigationLink({
    name: props.text,
    url: props.url,
  })
}

// TODO: Migrated for logHover if required. Delete both if not needed.
// const trackMouseEnter = (event: Event, linkText: string, url: string) => {
//   if (props.logHover) {
//     pushToDataLayer({
//       event: 'custom.menu.open',
//       label: linkText,
//       ...restGaOptions,
//       info: {
//         ...optionsInfo,
//         element: event.target as HTMLElement,
//         url,
//       },
//     })
//   }
// }

const trackClick = (event: Event) => {
  if (props.logClick && eventName) {
    const payload: Record<string, any> = {
      label: props.text,
      ...restGaOptions,
      info: {
        url: localeURL,
        element: (event?.target as HTMLElement)?.parentElement?.tagName,
        ...info,
      },
    }
    runtimeProps?.forEach((prop) => (payload[prop.name] = prop.value()))
    pushEventToDataLayer(eventName as DataLayerEventName, payload)
  }
}
</script>

<template>
  <!-- TODO: Since contentful can now be called from the clientside via middleware, 
   and we are no longer in a migration we can opt into nuxt clientside routing using NuxtLink which 
   can utalise pre-rendering to drastically speed up navigation, I tried this however 
   we need a solution to the fact that when using NuxtLink the layoutFiles only reloads 
   on full refresh or layout change so would require moving some things around such as init of accesstokens 
   or refreshing token etc to work correctly  -->
  <a
    ref="anchorElement"
    :href="localeURL"
    ontouchstart.passive=""
    v-bind="{ ...$attrs, ...targetObj, class: classes }"
    @click="trackClick($event)"
    class="anchor"
  >
    <Icon v-if="icon" v-bind="icon" />
    <slot>{{ text }}</slot>
    <svg-icon v-if="classes === 'link-text'" class="anchor__arrow-icon" name="mhj:arrow-right" />
  </a>
</template>

<style lang="postcss">
.anchor {
  /* set as display none here as this is to be overriden only by the menu */
  .anchor__arrow-icon {
    display: none;
  }
}
</style>
